import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { JobOfferData } from '../../models/interview';
import { downloadJobOfferDocument } from '../../services/job.service';
import { toast } from 'sonner';
import { MapPin, Briefcase, GraduationCap, Clock, Download } from 'lucide-react';
import { Button } from '../ui/atoms/Button';
import { Tag } from '../ui/atoms/Tag';
import { Typography } from '../ui/atoms/Typography';

interface InterviewJobDetailsProps {
  jobOffer: JobOfferData;
  workspacePrimaryColor?: string | null;
  workspaceSecondaryColor?: string | null;
}

export const InterviewJobDetails = ({
  jobOffer,
  workspacePrimaryColor,
  workspaceSecondaryColor,
}: InterviewJobDetailsProps) => {
  const { t } = useTranslation('interview');
  const [isDownloading, setIsDownloading] = useState(false);
  const isValidHexColor = (value?: string | null): value is string =>
    Boolean(value && /^#[0-9A-Fa-f]{6}$/.test(value));
  const hasBrandedButtonColors =
    isValidHexColor(workspacePrimaryColor) &&
    isValidHexColor(workspaceSecondaryColor);
  const brandedButtonStyle = hasBrandedButtonColors
    ? {
        backgroundColor: workspacePrimaryColor,
        borderColor: workspacePrimaryColor,
        color: workspaceSecondaryColor,
      }
    : undefined;

  const handleJobOfferDownload = async () => {
    try {
      setIsDownloading(true);
      const response = await downloadJobOfferDocument(jobOffer.id);
      if (!response.data || response.error) {
        throw new Error(response.error || 'Failed to download job offer document');
      }
      const blob = response.data;

      const sanitize = (text: string) =>
        text ? text.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '') : '';

      const jobTitle = sanitize(jobOffer.title);
      const companyName = sanitize(jobOffer.company_name || 'company');

      let extension = '.pdf';
      if (blob.type) {
        const mimeType = blob.type.toLowerCase();
        if (mimeType.includes('word') || mimeType.includes('docx')) extension = '.docx';
        else if (mimeType.includes('msword') || mimeType.includes('doc')) extension = '.doc';
        else if (mimeType.includes('markdown')) extension = '.md';
        else if (mimeType.includes('text/plain')) extension = '.txt';
      }

      const filename = `${jobTitle}_${companyName}${extension}`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(t('job_details.download_success'));
    } catch (error) {
      if (error instanceof Error && error.message.includes('404')) {
        toast.error(t('job_details.download_not_found'));
      } else {
        toast.error(t('job_details.download_failed'));
      }
    } finally {
      setIsDownloading(false);
    }
  };

  // Helper to format enum-like strings (e.g., full_time -> Full Time)
  const formatEnumString = (str: string | undefined): string => {
    if (!str) return t('job_details.not_available');
    return str.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const renderBadgeList = (items: (string | { name: string; importance: string })[] | undefined) => {
    if (!items || items.length === 0) return null;
    return (
      <div className="flex flex-wrap items-start content-start gap-2 mt-2 min-w-0">
        {items.map((item, index) => {
          const name = typeof item === 'string' ? item : item.name;
          return (
            <Tag key={`${name}-${index}`} intent="secondary" variant="subtle" wrap>
              {name}
            </Tag>
          );
        })}
      </div>
    );
  };

  const skillStack =
    jobOffer.skills && jobOffer.skills.length > 0
      ? jobOffer.skills
      : [...(jobOffer.coding_languages || []), ...(jobOffer.frameworks_libraries || [])];
  return (
    <div className="flex flex-col gap-8">
      {/* Header Section: Title & Core Meta */}
      <div>
        <div className="flex items-start justify-between gap-4 mb-4">
          <Typography as="h2" variant="h2" color="primary">
            {jobOffer.title}
          </Typography>
          <Button
            onClick={handleJobOfferDownload}
            label={isDownloading ? t('job_details.downloading') : t('job_details.download')}
            type="contained"
            intent="secondary"
            disabled={isDownloading}
            icon={<Download/>}
            className="flex-shrink-0 text-sm !border-0 ![box-shadow:none] ![--inv-stroke:transparent]"
            style={brandedButtonStyle}
            id="download-job-offer-btn"
          />
        </div>

        <div className="flex flex-wrap items-center gap-4">
          {jobOffer.company_name && (
            <div className="flex items-center gap-1.5">
              <Briefcase className="w-4 h-4 text-dark-invirtus/70" />
              <Typography as="span" variant="body-sm" color="secondary">
                {jobOffer.company_name}
              </Typography>
            </div>
          )}
          {jobOffer.location && (
            <div className="flex items-center gap-1.5">
              <MapPin className="w-4 h-4 text-dark-invirtus/70" />
              <Typography as="span" variant="body-sm" color="secondary">
                {jobOffer.location}
              </Typography>
            </div>
          )}
          {jobOffer.contract_type && (
            <div className="flex items-center gap-1.5">
              <Clock className="w-4 h-4 text-dark-invirtus/70" />
              <Typography as="span" variant="body-sm" color="secondary">
                {formatEnumString(jobOffer.contract_type)}
              </Typography>
            </div>
          )}
          {jobOffer.required_degree && (
            <div className="flex items-center gap-1.5">
              <GraduationCap className="w-4 h-4 text-dark-invirtus/70" />
              <Typography as="span" variant="body-sm" color="secondary">
                {jobOffer.required_degree}
              </Typography>
            </div>
          )}
        </div>
      </div>

      <hr className="border-gray-200/60" />

      {/* Main Two-Column Content */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 min-w-0">

        {/* Left Column: Descriptions */}
        <div className="md:col-span-2 space-y-8 min-w-0">
          {jobOffer.description && (
            <section className="flex flex-col gap-1.5">
              <Typography as="h4" variant="h5" color="primary">
                {t('job_details.the_role')}
              </Typography>
              <Typography as="p" variant="body-sm" color="secondary" className="leading-relaxed">
                {jobOffer.description}
              </Typography>
            </section>
          )}

          {jobOffer.mission_role_in_job && (
            <section className="flex flex-col gap-1.5">
              <Typography as="h4" variant="h5" color="primary">
                {t('job_details.mission')}
              </Typography>
              <Typography as="p" variant="body-sm" color="secondary" className="leading-relaxed">
                {jobOffer.mission_role_in_job}
              </Typography>
            </section>
          )}

          {jobOffer.company_description && (
            <section className="flex flex-col gap-1.5">
              <Typography as="h4" variant="h5" color="primary">
                {t('job_details.about_company')}
              </Typography>
              <Typography as="p" variant="body-sm" color="secondary" className="leading-relaxed">
                {jobOffer.company_description}
              </Typography>
            </section>
          )}
        </div>

        {/* Right Column: Tags & Requirements */}
        <div className="md:col-span-1 space-y-6 h-fit min-w-0">
          <section className="flex flex-col gap-1.5">
            <Typography as="h4" variant="h5" color="primary">
              {t('job_details.skill_stack')}
            </Typography>
            {renderBadgeList(skillStack)}
          </section>

          {jobOffer.soft_skills && jobOffer.soft_skills.length > 0 && (
            <section className="flex flex-col gap-1.5">
              <Typography as="h4" variant="h5" color="primary">
                {t('job_details.soft_skills')}
              </Typography>
              {renderBadgeList(jobOffer.soft_skills)}
            </section>
          )}

          {jobOffer.business_knowledge && jobOffer.business_knowledge.length > 0 && (
            <section className="flex flex-col gap-1.5">
              <Typography as="h4" variant="h5" color="primary">
                {t('job_details.business_domain')}
              </Typography>
              {renderBadgeList(jobOffer.business_knowledge)}
            </section>
          )}
        </div>
      </div>
    </div>
  );
}; 
